import React, { useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import style from "../styles/index.module.css";

import Modal from "@/components/Modal";
import { InputField } from "@/components/InputField";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import SelectCountry from "@/components/SelectCountry";

import { IButtonType, IButtonVariant } from "@/models/button";
import { IMobileVerify } from "@/models/onboarding";
import { ISelectCountryType } from "@/models";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { useAppSelector } from "@/store/hooks";

interface IProps {
  isOpen: boolean;
  mobileSubmitHandler: (values: IMobileVerify, countryCode: string) => void;
  closeHandler: () => void;
}

const VerifyPatientContact = (props: IProps) => {
  const { isLoading } = useAppSelector((state) => state.patientExistOrNot);
  const [phone, setPhone] = useState({
    number: "",
    countryCode: "",
    mobileLength: "",
  });

  const { isOpen, mobileSubmitHandler, closeHandler } = props;

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const intl = useIntl();

  const handleChangeMobileNo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, "");
    setPhone({ ...phone, number: value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (phone.number.trim().length === parseInt(phone?.mobileLength)) {
      mobileSubmitHandler({ mobileNo: phone.number }, phone.countryCode);
    }
  };

  const mobileInputField = () => {
    return (
      <>
        <DialogTitle>
          <Typography
            variant={"h5"}
            fontWeight={500}
            display={{ lg: "block", xs: "none" }}
          >
            <FormattedMessage
              id={"admit_patient"}
              defaultMessage="headerNavigationText"
            />
          </Typography>
          <Stack
            direction={"row"}
            alignItems={"center"}
            display={{ lg: "none", xs: "flex" }}
          >
            <IconButton
              sx={{ color: "var(--black-900)" }}
              onClick={closeHandler}
            >
              <ArrowBackIosIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="subtitle2" fontWeight={500}>
              <FormattedMessage id="admit_patient" />
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack gap={2.5}>
            <Typography component={"h3"} className={style.heading}>
              <FormattedMessage id={"enter_patient_contact"} />
            </Typography>

            <Stack
              direction={"row"}
              gap={1}
              alignItems={"center"}
              width={"100%"}
            >
              <SelectCountry
                isSmallScreen={isMobile || isTablet}
                countryCodeAndMobileLength={`${phone?.countryCode}_${phone?.mobileLength}`}
                setCountryCodeAndMobileLength={(
                  countryCode: string,
                  mobileLength: string
                ) => setPhone({ ...phone, countryCode, mobileLength })}
                type={ISelectCountryType.MAKE_APPOINTMENT_MOBILE_OR_TABLET}
              />

              <InputField
                type="text"
                inputMode="numeric"
                inputProps={{
                  maxLength: parseInt(phone?.mobileLength),
                  pattern: "[0-9]*",
                }}
                value={phone.number}
                onChange={handleChangeMobileNo}
                label={intl.formatMessage({
                  id: "mobileNumberText",
                  defaultMessage: "Mobile Number",
                })}
                variant="outlined"
                placeholder={intl.formatMessage({
                  id: "mobileNumberText",
                  defaultMessage: "Mobile Number",
                })}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack
            direction={"row"}
            gap={2}
            alignItems={"flex-end"}
            width={{ xs: "100%", lg: "50%" }}
            px={2}
          >
            {!(isMobile || isTablet) && (
              <Button
                btnTrigger={closeHandler}
                variant={IButtonVariant.WHITE}
                type={IButtonType.SUBMIT}
                disabled={isLoading}
              >
                <FormattedMessage id={"cancel"} defaultMessage="Cancel" />
              </Button>
            )}
            <Button
              variant={
                phone.number.trim().length !== parseInt(phone?.mobileLength)
                  ? IButtonVariant.GREY
                  : IButtonVariant.ORANGE
              }
              btnTrigger={handleSubmit}
              disabled={
                phone.number.trim().length !== parseInt(phone?.mobileLength) ||
                isLoading
              }
              isLoading={isLoading}
            >
              <FormattedMessage id={"check_number"} />
            </Button>
          </Stack>
        </DialogActions>
      </>
    );
  };

  return (
    <>
      {isMobile || isTablet ? (
        <Modal
          isOpen={isOpen}
          closeHandler={closeHandler}
          lineBottomBackground="white"
        >
          {mobileInputField()}
        </Modal>
      ) : (
        <Dialog
          fullWidth
          maxWidth="sm"
          isOpen={isOpen}
          closeHandler={closeHandler}
          paperProps={{
            sx: {
              pt: 2,
              pb: 2,
              maxWidth: "72rem",
            },
          }}
        >
          {mobileInputField()}
        </Dialog>
      )}
    </>
  );
};

export default VerifyPatientContact;
